type MuxSourceInfo = { type: 'mux'; src: string; preload: 'none'; muxPlaybackId: string; poster: string };
type CfsSourceInfo = { type: 'cfs'; src: string; preload: 'none'; poster: string };
type GenericSourceInfo = { type: 'other'; src: string; preload: 'auto' };
export type VideoSourceInfo = MuxSourceInfo | CfsSourceInfo | GenericSourceInfo;

/**
 * Regular expression that matches a Cloudflare Stream video URL. The named groups can
 * be used to construct other CFS resource URLS:
 * 	1. Account ID
 * 	2. Cloudflare UID
 */
const CFS_VIDEO_RE = /https?:\/\/customer-(\w+)\.cloudflarestream\.com\/(\w+)\//;

/**
 * Regular expression that matches a Mux Stream video URL and captures the playback ID.
 */
const MUX_VIDEO_RE = /^https?:\/\/stream\.mux\.com\/([a-zA-Z0-9]+)/;

/**
 * See: https://www.w3.org/TR/media-frags/#URIfragment-user-agent
 */
const URI_FRAGMENT = '#t=0.0001';

/**
 * Parses additional video source info from a video URL.
 *
 * For non-Cloudflare URLs, this returns the `src` property that should be used on the video
 * element. The video can be preloaded by the browser to display a frame as a poster. We add a tiny
 * fragment to the URL to offset the first frame which is sometimes blank.
 *
 *
 * For CloudflareStream URLs we are able to build extra information based on their API. The poster
 * image allows us to avoid preloading the video and (hopefully) reduce cost. See:
 * https://developers.cloudflare.com/api/operations/stream-videos-retrieve-video-details
 *
 * For Mux URLs, we extract the playback ID so the MuxPlayer component can be used directly.
 */
export function parseSourceInfoFromUrl(videoUrl: string): VideoSourceInfo {
	const muxMatch = MUX_VIDEO_RE.exec(videoUrl);

	if (muxMatch) {
		const [, playbackId] = muxMatch;

		return {
			type: 'mux',
			src: videoUrl,
			preload: 'none',
			muxPlaybackId: playbackId,
			poster: `https://image.mux.com/${playbackId}/thumbnail.jpg?time=0.1`,
		};
	}

	const cfsMatch = CFS_VIDEO_RE.exec(videoUrl);

	if (!cfsMatch) {
		return {
			type: 'other',
			src: videoUrl + URI_FRAGMENT,
			preload: 'auto',
		};
	}

	const [, accountId, cloudflareUid] = cfsMatch;
	const cfsThumbnailUrl = `https://customer-${accountId}.cloudflarestream.com/${cloudflareUid}/thumbnails/thumbnail.jpg`;

	return {
		type: 'cfs',
		src: videoUrl,
		preload: 'none',
		poster: cfsThumbnailUrl,
	};
}
